/**
 * The offline-attendance context service
 * (`specs/offline-attendance-v1/implementation-plan.md`, task FE-7; decisions
 * D2, D3, D5, D7; amendments C2, C10–C12).
 *
 * Three operations over ONE scope (`offline-scope.ts`), each the only way its
 * data enters or leaves the sealed repository:
 *
 *   - {@link provision} — `GET /v1/api/me/attendance/offline-context`: what
 *     authorizes offline capture. The answer is decoded strictly, must be for
 *     THIS scope, must keep the employee the scope was provisioned for, and —
 *     when capture is on — must carry an ES256 grant that verifies against the
 *     answer's own JWKS and is bound to this scope and policy. Only then is it
 *     calibrated against the answer's timings and saved; any refusal writes
 *     NOTHING. A capability that is OFF is saved too (C2): it replaces a stored
 *     enabled context, so a phone that reconnects stops capturing.
 *   - {@link refreshState} — `GET /v1/api/me/attendance/sync/state`: the cheap
 *     confirmed-snapshot refresh (no grant, no calibration).
 *   - {@link loadLocal} — what is on the device, re-verified, for the
 *     orchestrator to start from before (or without) any network.
 *
 * Load-bearing rules:
 *   - The confirmed base comes only from these NetworkOnly routes (D7). A
 *     snapshot never replaces a newer one — in storage (the repository's
 *     `asOfMs` rule) or in what these functions answer.
 *   - The grant ISSUER is pinned by the scope's first enabled provisioning
 *     (D3); a later grant from another issuer is refused (`issuer_mismatch`),
 *     and the pin survives disabled periods. The grant's validity window is
 *     taken from its SIGNED claims (`iat`/`vfr`/`vut`), never from the unsigned
 *     envelope beside it. Only authenticity is checked here: whether the window
 *     allows a capture is readiness's call, made with the corrected clock.
 *   - The calibration is stored exactly as measured, even when it is too
 *     imprecise to stamp with: the clock monitor decides whether it is used
 *     (C10/C11). Timings that cannot be calibrated at all refuse the answer.
 *   - A session that ended, or a database connection that ended (another tab
 *     deleted or upgraded it, the browser closed it), while a request ran saves
 *     nothing (`stale`): the key and the scope it would write under may belong
 *     to a world that is gone. The orchestrator re-resolves and asks again.
 *   - {@link loadLocal} never hands out a record that fails its check; it
 *     reports `scope_mismatch` (C12) and readiness turns that into "set
 *     offline punches up again", which the next provisioning does.
 *
 * Failures:
 *   - ERPat's whole-request refusals map to `unsupported` (a 404 that is not
 *     `module_unavailable` — the route is missing — or 422 `unsupported_schema`),
 *     `module_unavailable` (404), `forbidden` (403 `forbidden` or
 *     `account_inactive`). Everything else the transport throws — no network, a
 *     timeout, a sign-out, an edge check, rate limiting, 5xx, a web page — is
 *     `transport` (try again later), with the transport's error.
 *   - `invalid` is an answer that came back but cannot be trusted. Its reason
 *     is one of: `malformed:<decoder key>`, `scope_mismatch`,
 *     `prior_scope_mismatch` (the caller passed another scope's record),
 *     `employee_mismatch`, `issuer_mismatch`, `grant:<GrantFailureReason>`,
 *     `calibration`, and — refresh only — `context_unreadable` (the stored
 *     context to compare the employee with does not open).
 *   - These functions reject only with the repository's `StorageError` (a
 *     local IndexedDB failure while saving or loading), never for the network.
 *     When {@link provision} verified an answer and then failed to SAVE it,
 *     {@link unsavedCapability} still names what that answer said about the
 *     capability (and nothing else of it).
 */
import {
  decodeOfflineContext,
  decodeSyncState,
  type OfflineCapabilityV1,
  type ScopeEchoV1,
} from '@/contracts/offline-attendance-v1';
import { getSessionEpoch } from '@/services/api/erpat-client';
import {
  OfflineApiError,
  fetchOfflineContext,
  fetchSyncState,
  type Timed,
} from '@/services/api/offline-attendance.api';
import { calibrate } from '@/services/offline/clock-health';
import { verifyGrant } from '@/services/offline/grant-verify';
import { sameOfflineScope } from '@/services/offline/offline-scope';
import { normalizeTenant } from '@/services/session-scope';
import {
  loadContext,
  loadSnapshot,
  saveContext,
  saveSnapshot,
  type StorageErrorKind,
} from '@/services/storage/attendance-storage';
import { offlineDbGeneration } from '@/services/storage/db';
import type { DataKeyReady } from '@/services/storage/offline-keys';
import type {
  ClockCalibration,
  ConfirmedSnapshotRecord,
  OfflineContextRecord,
  OfflineScope,
  StoredGrant,
} from '@/types/offline-attendance';
import { APP_VERSION } from '@/utils/env';

/* ── Results ────────────────────────────────────────────────────────────── */

/** What moved while a request ran: the session (sign-out) or the database connection. */
export type StaleReason = 'session' | 'database';

/** ERPat refused the whole request (see the module docblock for the mapping). */
export type RefusalResult =
  | { kind: 'unsupported'; error: OfflineApiError }
  | { kind: 'forbidden'; error: OfflineApiError }
  | { kind: 'module_unavailable'; error: OfflineApiError };

/** No usable answer: the network, the session, or the request was aborted. Try again later. */
export type TransportResult = { kind: 'transport'; error: OfflineApiError };
/** An answer came back but cannot be trusted; `reason` names why (module docblock). */
export type InvalidResult = { kind: 'invalid'; reason: string };
/** The session or the database changed while the request ran; nothing was saved. */
export type StaleResult = { kind: 'stale'; reason: StaleReason };

export type ProvisionResult =
  | { kind: 'enabled'; record: OfflineContextRecord; snapshot: ConfirmedSnapshotRecord }
  | { kind: 'disabled'; record: OfflineContextRecord; snapshot: ConfirmedSnapshotRecord }
  | RefusalResult
  | TransportResult
  | InvalidResult
  | StaleResult;

export type StateResult =
  | {
      kind: 'ok';
      /** The NEWEST confirmed snapshot the scope has — this answer's, or a newer one already stored. */
      snapshot: ConfirmedSnapshotRecord;
      /** `capability.offline_attendance` of this answer. */
      capability: boolean;
      /** `capability.reason` (null exactly when `capability` is true). */
      reason: OfflineCapabilityV1['reason'];
    }
  | RefusalResult
  | TransportResult
  | InvalidResult
  | StaleResult;

/** `scope_mismatch`: the stored context failed its check and is withheld (`context` is null). */
export type ContextIntegrity = 'ok' | 'scope_mismatch';

export interface LocalContext {
  /** The scope's context, re-verified; `null` when never provisioned or when it failed its check. */
  context: OfflineContextRecord | null;
  /** The scope's confirmed snapshot; `null` when none is stored or the stored one does not open. */
  snapshot: ConfirmedSnapshotRecord | null;
  /** Readiness input C12 (`ReadinessInputs.contextIntegrity`). */
  integrity: ContextIntegrity;
}

/* ── Provisioning ───────────────────────────────────────────────────────── */

/**
 * Ask ERPat for this scope's offline context and save it when it can be trusted.
 *
 * `prior` is the scope's current context (from {@link loadLocal}, or `null`):
 * the answer must keep its employee, and an enabled answer its pinned issuer.
 * A `prior` of another scope is refused (`prior_scope_mismatch`).
 *
 * On `enabled` / `disabled` the record and the snapshot are saved (the snapshot
 * only when it is at least as new as the stored one) and answered; the snapshot
 * answered is the NEWEST the scope has. The caller then (C11), with no unrelated
 * `await` in between, gives the clock monitor the new policy's thresholds and
 * `calibrated(record.calibration)`. A save that fails rejects with the
 * repository's `StorageError`; {@link unsavedCapability} names the capability
 * of the answer it could not keep.
 */
export async function provision(
  scope: OfflineScope,
  key: DataKeyReady,
  prior: OfflineContextRecord | null,
  signal?: AbortSignal,
): Promise<ProvisionResult> {
  const unchanged = watchForChange(signal);
  if (prior !== null && !sameOfflineScope(prior.scope, scope)) return invalid('prior_scope_mismatch');

  let answer: Timed<unknown>;
  try {
    answer = await fetchOfflineContext(scope.installationId, APP_VERSION, signal);
  } catch (err) {
    // The transport throws only OfflineApiError; anything else is a bug and is rethrown.
    return refusalOf(err);
  }

  const decoded = decodeOfflineContext(answer.data);
  if (!decoded.ok) return invalid(`malformed:${decoded.reason}`);
  const ctx = decoded.value;
  if (!echoNames(ctx.scope, scope)) return invalid('scope_mismatch');
  if (prior !== null && ctx.scope.employee_id !== prior.employeeId) return invalid('employee_mismatch');

  let issuer = prior?.issuer ?? '';
  let grant: StoredGrant | null = null;
  if (ctx.capability.offline_attendance) {
    // The decoder guarantees both beside an enabled capability; narrowed for the compiler.
    if (ctx.grant === null) return invalid('malformed:grant');
    if (ctx.policy === null) return invalid('malformed:policy');
    const verdict = await verifyGrant(
      ctx.grant.token,
      ctx.keys,
      {
        companyKey: scope.companyKey,
        userId: scope.userId,
        installationId: scope.installationId,
        grantId: ctx.grant.grant_id,
        // The pin is compared below, so that a changed issuer reads as such.
        issuer: null,
        policyVersion: ctx.policy.version,
      },
      Date.now(),
      false,
    );
    if (!verdict.ok) return invalid(`grant:${verdict.reason}`);
    const { claims } = verdict;
    if (issuer !== '' && claims.iss !== issuer) return invalid('issuer_mismatch');
    issuer = claims.iss;
    grant = {
      grantId: ctx.grant.grant_id,
      token: ctx.grant.token,
      kid: ctx.grant.kid,
      issuedAtMs: claims.iat * 1000,
      validFromMs: claims.vfr * 1000,
      validUntilMs: claims.vut * 1000,
    };
  }

  let calibration: ClockCalibration;
  try {
    calibration = calibrate(
      ctx.server_time,
      answer.sentDeviceMs,
      answer.receivedDeviceMs,
      answer.sentPerfMs,
      answer.receivedPerfMs,
    );
  } catch {
    // RangeError: the timings cannot place the server time on the device clock.
    return invalid('calibration');
  }

  const record: OfflineContextRecord = {
    scope: { ...scope },
    issuer,
    employeeId: ctx.scope.employee_id,
    displayName: ctx.user.display_name,
    capability: { offlineAttendance: ctx.capability.offline_attendance, reason: ctx.capability.reason },
    grant,
    keys: [...ctx.keys],
    policy: ctx.policy,
    calibration,
    provisionedAtMs: answer.receivedDeviceMs,
  };
  const snapshot = snapshotRecord(ctx.confirmed, answer.receivedDeviceMs);

  const changed = unchanged();
  if (changed) return changed;
  try {
    await saveContext(key, scope, record, snapshot);
  } catch (err) {
    // The answer was verified — this scope, this employee, a grant that holds,
    // timings that calibrate — so what it says about the capability is true even
    // though the device could not keep it (see unsavedCapability()).
    if (typeof err === 'object' && err !== null) unsavedAnswers.set(err, { ...record.capability });
    throw err;
  }
  return {
    kind: ctx.capability.offline_attendance ? 'enabled' : 'disabled',
    record,
    snapshot: await newestSnapshot(key, scope.scopeKey, snapshot),
  };
}

/** The capability of each verified answer whose save failed, keyed by the error {@link provision} rejected with. */
const unsavedAnswers = new WeakMap<object, OfflineContextRecord['capability']>();

/**
 * What a VERIFIED provisioning answer said about the capability when saving it
 * failed — `err` being the repository's `StorageError` {@link provision}
 * rejected with — else `null` (any other error, or an answer refused before the
 * save). The record itself was not kept and must not be used; the capability
 * alone lets a caller say what ERPat said while the device's storage fails (a
 * company that turned capture off keeps ERPat's own POST).
 */
export function unsavedCapability(err: unknown): OfflineContextRecord['capability'] | null {
  if (typeof err !== 'object' || err === null) return null;
  return unsavedAnswers.get(err) ?? null;
}

/* ── State refresh ──────────────────────────────────────────────────────── */

/**
 * Refresh this scope's confirmed snapshot from ERPat. The answer must be for
 * THIS scope and — when a context is stored — for its employee. A newer
 * snapshot is saved; `ok` always answers the NEWEST the scope has, so a late,
 * older answer never rolls the base back. The capability is reported, not
 * saved: switching capture off for good is the next provisioning's job.
 */
export async function refreshState(scope: OfflineScope, key: DataKeyReady, signal?: AbortSignal): Promise<StateResult> {
  const unchanged = watchForChange(signal);

  let answer: Timed<unknown>;
  try {
    answer = await fetchSyncState(scope.installationId, signal);
  } catch (err) {
    // The transport throws only OfflineApiError; anything else is a bug and is rethrown.
    return refusalOf(err);
  }

  const decoded = decodeSyncState(answer.data);
  if (!decoded.ok) return invalid(`malformed:${decoded.reason}`);
  const state = decoded.value;
  if (!echoNames(state.scope, scope)) return invalid('scope_mismatch');

  let stored: OfflineContextRecord | null;
  try {
    stored = await loadContext(key, scope.scopeKey);
  } catch (err) {
    // A context that does not open cannot vouch for the employee; any other failure is the caller's.
    if (isStorageError(err, 'corrupt')) return invalid('context_unreadable');
    throw err;
  }
  if (stored !== null && stored.employeeId !== state.scope.employee_id) return invalid('employee_mismatch');

  const snapshot = snapshotRecord(state.confirmed, answer.receivedDeviceMs);
  const changed = unchanged();
  if (changed) return changed;
  const saved = await saveSnapshot(key, scope.scopeKey, snapshot);
  return {
    kind: 'ok',
    snapshot: saved ? snapshot : await newestSnapshot(key, scope.scopeKey, snapshot),
    capability: state.capability.offline_attendance,
    reason: state.capability.reason,
  };
}

/* ── Local load ─────────────────────────────────────────────────────────── */

/**
 * The scope's context and confirmed snapshot as stored on this device — no
 * network. The context is re-checked first: its scope must be the one asked
 * for, and its grant (if any) must still verify, without a validity check,
 * against the stored keys, bound to this scope, the pinned issuer and the
 * stored policy's version, with the stored window equal to the signed one. A
 * context that fails — or does not open at all — is withheld (`context: null`)
 * and reported as `integrity: 'scope_mismatch'`. A snapshot that does not open
 * reads as none. Rejects only with the repository's `StorageError` for an
 * IndexedDB failure (`unavailable`, `aborted`, …).
 */
export async function loadLocal(scope: OfflineScope, key: DataKeyReady): Promise<LocalContext> {
  let context: OfflineContextRecord | null = null;
  let integrity: ContextIntegrity = 'ok';
  try {
    const record = await loadContext(key, scope.scopeKey);
    if (record !== null) {
      if (await recordHolds(record, scope)) context = record;
      else integrity = 'scope_mismatch';
    }
  } catch (err) {
    // A stored context that does not open is an integrity problem the next provisioning repairs.
    if (!isStorageError(err, 'corrupt')) throw err;
    integrity = 'scope_mismatch';
  }

  let snapshot: ConfirmedSnapshotRecord | null = null;
  try {
    snapshot = await loadSnapshot(key, scope.scopeKey);
  } catch (err) {
    // An unreadable snapshot is simply none: the next provisioning or refresh stores a fresh one.
    if (!isStorageError(err, 'corrupt')) throw err;
  }

  return { context, snapshot, integrity };
}

/* ── Internals ──────────────────────────────────────────────────────────── */

function invalid(reason: string): InvalidResult {
  return { kind: 'invalid', reason };
}

/**
 * Capture what must not change while a request runs — the session epoch, the
 * database connection generation and the caller's signal — and return the check
 * to run right before saving: `null` when nothing changed.
 */
function watchForChange(signal: AbortSignal | undefined): () => TransportResult | StaleResult | null {
  const epoch = getSessionEpoch();
  const generation = offlineDbGeneration();
  return () => {
    if (signal?.aborted) return { kind: 'transport', error: new OfflineApiError('cancelled', 0, { cancelled: true }) };
    if (getSessionEpoch() !== epoch) return { kind: 'stale', reason: 'session' };
    if (offlineDbGeneration() !== generation) return { kind: 'stale', reason: 'database' };
    return null;
  };
}

/** Classify a transport failure (rethrowing anything that is not one). */
function refusalOf(err: unknown): RefusalResult | TransportResult {
  if (!(err instanceof OfflineApiError)) throw err;
  if (err.status === 404) {
    return err.code === 'module_unavailable' ? { kind: 'module_unavailable', error: err } : { kind: 'unsupported', error: err };
  }
  if (err.status === 422 && err.code === 'unsupported_schema') return { kind: 'unsupported', error: err };
  if (err.status === 403 && (err.code === 'forbidden' || err.code === 'account_inactive')) {
    return { kind: 'forbidden', error: err };
  }
  return { kind: 'transport', error: err };
}

/** ERPat's scope echo names this scope (the company key normalized on both sides; ids decoded). */
function echoNames(echo: ScopeEchoV1, scope: OfflineScope): boolean {
  return (
    normalizeTenant(echo.company_key) === normalizeTenant(scope.companyKey) &&
    echo.user_id === scope.userId &&
    echo.installation_id === scope.installationId
  );
}

function snapshotRecord(snapshot: ConfirmedSnapshotRecord['snapshot'], fetchedAtMs: number): ConfirmedSnapshotRecord {
  return { snapshot, asOfMs: Date.parse(snapshot.as_of), fetchedAtMs };
}

/**
 * The newest of `fetched` and what the scope now stores. A stored snapshot that
 * cannot be read (it blocked the write) never costs the caller the fresh one.
 */
async function newestSnapshot(
  key: DataKeyReady,
  scopeKey: string,
  fetched: ConfirmedSnapshotRecord,
): Promise<ConfirmedSnapshotRecord> {
  let stored: ConfirmedSnapshotRecord | null;
  try {
    stored = await loadSnapshot(key, scopeKey);
  } catch {
    // The save already succeeded or was refused as older; the answer itself is still sound.
    return fetched;
  }
  return stored !== null && stored.asOfMs > fetched.asOfMs ? stored : fetched;
}

/** The stored record still holds for `scope` (see {@link loadLocal}). */
async function recordHolds(record: OfflineContextRecord, scope: OfflineScope): Promise<boolean> {
  if (!sameOfflineScope(record.scope, scope)) return false;
  const { grant } = record;
  if (grant === null) return true;
  // Provisioning pins the issuer of every grant it stores.
  if (record.issuer === '') return false;
  const verdict = await verifyGrant(
    grant.token,
    record.keys,
    {
      companyKey: scope.companyKey,
      userId: scope.userId,
      installationId: scope.installationId,
      grantId: grant.grantId,
      issuer: record.issuer,
      policyVersion: record.policy?.version ?? null,
    },
    Date.now(),
    false,
  );
  if (!verdict.ok) return false;
  const { claims } = verdict;
  return (
    grant.issuedAtMs === claims.iat * 1000 &&
    grant.validFromMs === claims.vfr * 1000 &&
    grant.validUntilMs === claims.vut * 1000
  );
}

/**
 * A repository `StorageError` of `kind`. Compared by shape, not `instanceof`:
 * a second copy of the module (a test's `vi.resetModules()`) has its own class.
 */
function isStorageError(err: unknown, kind: StorageErrorKind): boolean {
  return err instanceof Error && err.name === 'StorageError' && Reflect.get(err, 'kind') === kind;
}
